

import { intervalToDuration, formatDuration, fromUnixTime, getUnixTime, addYears } from 'date-fns';
import { Badge, Button, Card, FormControl, FormGroup, FormLabel, ListGroup, ListGroupItem, FormSelect, Alert, Popover, Overlay, Container, Row, Col } from 'react-bootstrap';
import { useGetAllAutoMessagesQuery, useInstallAutomessageMutation } from '../api/messageAPI';
import { AutoMessage, StoredMessageTemplate, autoMessageTypes, TimeRecipe, isInTimeRecipe, isNextTimeRecipe, NextTimeRecipe, DayName, InTimeRecipe, AutoMessageType, MessageTemplate, NextTimeDefinition } from '../api/types/messages';
import { LoaderComponent } from '../components/LoaderComponent';
import { EditableMessage, MessageTemplateComponent, MessageTemplatesListEditor } from '../components/messages';
import { keyBadge, Icon, HelpTip, TimeHelper, DurationHelper } from '../components/ui';
import React, { useState, useRef, useEffect } from 'react';
import DatePicker from "react-datepicker";
import { secondsToHuman } from '../utils/time';
import { ExpressionArg } from 'survey-engine/data_types';
//import { ExpressionEditorState, createExpressionArgEditorState, ExpressionEditor } from "ifn-expression-editor";
import {  ExpressionPreviewArgComponent } from "ifn-expression-editor";
import { AutoMessageRecipeSelector, fromTimeRecipe } from '../components/messages/Automessages';
import DatalistInput from 'react-datalist-input';
import { useAvailableStudies } from '../hooks/useAvailableStudies';
import { useAutomessageRecipes } from '../hooks';
import { UpdateResultComponent } from '../components/utils/UpdateResultComponent';
import { isValidExpressionArg } from '../utils';

interface AutoMessageListProps {
   automessages: AutoMessage[];
}

/***
 * List messages from the platform
 */
export const AutoMessageList: React.FC<AutoMessageListProps> = (props) => {

   const toDuration = (seconds: number) => {
      return formatDuration(intervalToDuration({ 'start': 0, 'end': seconds * 1000 }));
   }

   const toTime = (time: TimeRecipe) => {
      if (typeof (time) == "object") {
         return JSON.stringify(time);
      }
      time = +time;
      // On the platform times are always number 
      return fromUnixTime(time).toLocaleString();
   }

   const showAutoMessage = (m: AutoMessage, index: number) => {
      return <ListGroupItem key={index}>
         <div className='my-1'>
            {m.id ? keyBadge(m.id) : ''}
            <span className='text-muted'>{m.label}</span>
            <Badge bg="warning" className='mx-1'><Icon name="bullseye" className='me-1' />{m.type}</Badge>
            <span className='me-1'><Icon name="calendar" className='me-1' />Start <Badge bg="info">{m.nextTime ? toTime(m.nextTime) : ''}</Badge></span>,
            every <span className='me-1'><Icon name="hourglass" className='me-1' /><Badge bg="info" title="Number of seconds">{m.period} s.</Badge> ({toDuration(m.period)})</span>
            {m.until ? <span><Icon name="calendar-times" className='me-1' /> Until <Badge bg="info">{toTime(m.until)}</Badge></span> : ''}
         </div>
         <div>
            {m.condition ? <div>Condition : <ExpressionPreviewArgComponent exp={m.condition} /></div> : ''}
            <MessageTemplateComponent template={m.template} preview={false} showId={false} />
         </div>
      </ListGroupItem>
   }

   return <ListGroup>
      {props.automessages ? props.automessages.map(showAutoMessage) : 'No automessage is installed'}
   </ListGroup>;
}

interface AutoMessageInstallerProps {
   template: StoredMessageTemplate;
   onClose: () => void
   onInstall: (m: AutoMessage) => void
}

const isFixedTime = (t: TimeRecipe): t is number => {
   return typeof (t) == "number";
}

const showTimeRecipe = (t: TimeRecipe): string => {
   const d = new Date(fromUnixTime(fromTimeRecipe(t, new Date(Date.now()))));
   return d.toLocaleString();
}

interface TimeRecipeWidgetProps {
   time: TimeRecipe
   label: string
   inSuffix: string
   minDate: Date
   maxDate: Date
   onChange: (v: TimeRecipe) => void;
}

interface NextTimeRecipeWidgetProps {
   time: NextTimeRecipe
   onChange: (t: NextTimeRecipe) => void;
}

export const NextTimeRecipeWidget: React.FC<NextTimeRecipeWidgetProps> = (props) => {
   const days: DayName[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

   const [recipe, setRecipe] = useState<NextTimeDefinition>(props.time.next);

   useEffect(()=> {
      console.log('next recipe', recipe);
      props.onChange({next: recipe});
   }, [recipe]);

   const changeDay = (d: string) => {
      console.log("changeDay", d);
      var day : DayName | undefined;
      if (d === "") {
         day = undefined;
      } else {
         const di = days.indexOf(d as DayName);
         if (di >= 0) {
            day = days[di];
         }
      }
      setRecipe({day: day, minute: recipe.minute, hour:recipe.hour});
   }

   const changeMinute = (d: string) => { 
      const v = d === "" ? undefined : +d;
      setRecipe({day: recipe.day, minute: v, hour:recipe.hour}); 
   }

   const changeHour = (d: string) => { 
      const v = d === "" ? undefined : +d;
      recipe.hour = v;
      setRecipe({day: recipe.day, minute: recipe.minute, hour:v}); 
   }

   const hours = Array.from(Array(24).keys());
   const minutes = Array.from(Array(60).keys());

   return <div>
      Next <select title="Day of the week" onChange={e => changeDay(e.currentTarget.value)} defaultValue={typeof (recipe.day) == "undefined" ? "" : recipe.day}>
         <option value="">-</option>
         {days.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      At
      <select title="Hour" onChange={e => changeHour(e.currentTarget.value)} className="mx-1" defaultValue={typeof (recipe.hour) == "undefined" ? "" : recipe.hour}>
         <option value="">-</option>
         {hours.map(d => <option key={d} value={d}>{d}</option>)}
      </select> :

      <select title="Minutes" onChange={e => changeMinute(e.currentTarget.value)} defaultValue={recipe.minute}>
         <option value="">-</option>
         {minutes.map(d => <option key={d} value={d}>{d}</option>)}
      </select> m
   </div>
}


interface InTimeRecipeWidgetProps {
   time: InTimeRecipe
   suffix: string
   onChange: (t: InTimeRecipe) => void;
}

export const InTimeRecipeWidget: React.FC<InTimeRecipeWidgetProps> = (props) => {

   const o = props.time.in;

  const [duration, setDuration] = useState<Duration>(o); 

  const updateDuration = (field: keyof Duration, value: string) => {
    console.log('update in field', field, value);  
      const v = value === "" ? undefined : +value;
     duration[field] = v;
     setDuration(duration);
  };

   useEffect(()=>{
      props.onChange({ in: duration });
   }, [duration]);
   
   const [show, setShow] = useState(false);
   const target = useRef(null);
   const ref = useRef(null);
   
   return <div ref={ref}>
      In {formatDuration(duration)} {props.suffix}
      <Button onClick={e => setShow(!show)} variant="info" size="sm" ref={target}>Editer</Button>
      <Overlay
         show={show}
         target={target}
         placement="bottom"
         container={ref}
         containerPadding={20}
      >
         <Popover>
            <Popover.Header as="h3">Edit duration time</Popover.Header>
            <Popover.Body>
               <Container>
                  <table className='table'>
                     <tbody>
                        <tr>
                           <td>years</td>
                           <td><input type="number" min={0} onChange={e => updateDuration('years', e.currentTarget.value)} value={duration.years} className="col-3" /></td>
                        </tr>
                        <tr>
                           <td>months</td><td><input type="number" min={0} onChange={e => updateDuration('months', e.currentTarget.value)} value={duration.months} className="col-3" /></td>
                        </tr>
                        <tr>
                           <td>weeks</td><td><input type="number" min={0} onChange={e => updateDuration('weeks', e.currentTarget.value)} value={duration.weeks} className="col-3" /></td>
                        </tr>
                        <tr>
                           <td>days</td><td><input type="number" min={0} onChange={e => updateDuration('days', e.currentTarget.value)} value={duration.days} className="col-3" /></td>
                        </tr>
                        <tr>
                           <td>hours</td><td><input type="number" min={0} onChange={e => updateDuration('hours', e.currentTarget.value)} value={duration.hours} className="col-3" /></td>
                        </tr>
                        <tr>
                           <td>minutes</td><td><input type="number" min={0} onChange={e => updateDuration('minutes', e.currentTarget.value)} value={duration.minutes} className="col-3" /></td>
                        </tr>
                        <tr>
                           <td>seconds</td><td><input type="number" min={0} onChange={e => updateDuration('seconds', e.currentTarget.value)} value={duration.seconds} className="col-3" /></td>
                        </tr>
                     </tbody>
                  </table>
               </Container>
            </Popover.Body>
         </Popover>
      </Overlay>
   </div>
}

export const TimeRecipeWidget: React.FC<TimeRecipeWidgetProps> = (props) => {
   return <React.Fragment>
      {isFixedTime(props.time) ?
         <DatePicker
            className="form-control w-auto shadow-none p-1"
            selected={fromUnixTime(props.time)}
            locale='default'
            onChange={(date) => date ? props.onChange(getUnixTime(date.getTime())) : ''}
            placeholderText={props.label}
            minDate={props.minDate}
            maxDate={props.maxDate}
            autoComplete="off"
            popperPlacement="top"
            showTimeSelect
            dateFormat="Pp"

         />
         : ''}
      {isInTimeRecipe(props.time) ? <InTimeRecipeWidget time={props.time} onChange={props.onChange} suffix={props.inSuffix}/> : ''}
      {isNextTimeRecipe(props.time) ? <NextTimeRecipeWidget time={props.time} onChange={props.onChange} /> : ''}
   </React.Fragment>
}

interface ConditionWidgetProps {
   condition?: ExpressionArg
   onChange: (arg?: ExpressionArg) =>void;
}

export const ConditionWidget: React.FC<ConditionWidgetProps> = (props) => {
   /*
   const [conditionEditorState, setConditionEditorState] = useState<ExpressionEditorState>(createExpressionArgEditorState(props.condition));

   useEffect(()=>{
      const state = createExpressionArgEditorState(props.condition);
      setConditionEditorState(state);
   }, [props.condition]);

   const handleExpressionEditorChange = (s: ExpressionEditorState) => {
      setConditionEditorState(s);
      props.onChange(s.getResult() as ExpressionArg);
   }

   console.log(conditionEditorState);
   
   return <div>
      {JSON.stringify(props.condition)}
      {JSON.stringify(conditionEditorState)}
      <ExpressionEditor state={conditionEditorState} mode="server" onChange={handleExpressionEditorChange} />  
   </div>
   */

  const check = props.condition ? isValidExpressionArg(props.condition) : true;
  const valide = typeof(check) == 'boolean';
  return <div>      
   { props.condition ? 
      ( valide ? <ExpressionPreviewArgComponent exp={props.condition}/> : <span className="text-danger">Bad Expression arg : {check}</span>  )
   : 'No condition' }
   </div>
}

export const AutoMessageInstaller: React.FC<AutoMessageInstallerProps> = (props) => {

   const [nextTime, setNextTime] = useState<TimeRecipe>(getUnixTime(Date.now()));
   const [untilTime, setUntilTime] = useState<TimeRecipe | undefined>();
   const [periodicity, setPeriodicity] = useState<number>(60 * 60 * 24);
   const [autoType, setAutoType] = useState<AutoMessageType>('all-users');
   const [label, setLabel] = useState<string>('');
   const [studyKey, setStudyKey] = useState<string|undefined>('');
   const [condition, setCondition] = useState<ExpressionArg | undefined>(undefined);
   const [forceTemplateType, setForceTemplateType] = useState<string|undefined>(undefined);
   const [errors, setErrors] = useState<string[] | undefined>(undefined);
   const minDate = new Date(Date.now());
   const maxDate = addYears(minDate, 1);
   
   const studies = useAvailableStudies();

   const recipes = useAutomessageRecipes();

   const getTemplate = () => {
      if(forceTemplateType) {
         const template = {...props.template};
         template.messageType = forceTemplateType;
         return template;
      }
      return props.template;
   }

   const handleInstall = () => {
      console.log("install click");
      const now = new Date(Date.now());
      
      const m: AutoMessage = {
         template: getTemplate(),
         type: autoType, // all-users - study-participants
         studyKey: studyKey ? studyKey : undefined,
         condition: condition,
         nextTime: fromTimeRecipe(nextTime, now), // when is the next time the message should be triggered
         until: typeof (untilTime) != "undefined" ? fromTimeRecipe(untilTime, now) : undefined, // When automessage should stop to be sent
         period: periodicity,   // how often it should be triggered
         label: label,
      }
      props.onInstall(m);
   }

   const applyRecipe = (m: Partial<AutoMessage>, forceTemplateType?: string) => {
      console.log('recipe', m);

      if('condition' in m) {
         setCondition(m.condition);
      }
      
      if ('label' in m) {
         setLabel(m.label ?? '');
      }

      if ('nextTime' in m && m.nextTime) {
         setNextTime(m.nextTime);
      }
      if ('until' in m) {
         setUntilTime(m.until);
      }
      if ('studyKey' in m) {
         setStudyKey(m.studyKey ?? '');
      }
      if ('period' in m && m.period) {
         setPeriodicity(m.period);
      }

      if ('type' in m && m.type) {
         setAutoType(m.type);
         if(m.type === 'all-users') {
            setStudyKey(undefined);
         }
      }
      setForceTemplateType(forceTemplateType);
      validate();
   };

   const validate = (): boolean => {
      const now = new Date();
      const ee: string[] = [];

      const next = fromUnixTime(fromTimeRecipe(nextTime, now));
      const until = untilTime ? fromUnixTime(fromTimeRecipe(untilTime, now)) : undefined;

      console.log('validate times', now, next, until);

      if (next < now) {
         ee.push('next time in the past');
      }
      if (until && until < now) {
         ee.push('until time in the past');
      }
      if(until && until < next) {
         ee.push("Until time is before next time");
      }

      if (ee.length) {
         setErrors(ee);
         return false;
      } else {
         setErrors(undefined);
         return true;
      }
   };

   const updateNextTime = (d: TimeRecipe) => {
      setNextTime(d);
      validate();
   }

   const updateUntilTime = (d?: TimeRecipe)=> {
      setUntilTime(d);
      validate();
   }

   const setAutoTypeFromString = (t: string) => {
      const i = autoMessageTypes.indexOf(t as AutoMessageType);
      if (i >= 0) {
         setAutoType(autoMessageTypes[i]);
      }
   }

   const enableInstaller = +(process.env['REACT_APP_AUTOMESSAGE_INSTALL'] ?? '0') > 0;

   return <Card className='mt-2 border-warning'>
      <Card.Header className="bg-warning">
         <h4>Automessage Installer</h4>
      </Card.Header>
      <Card.Body>
         <div className='row'>
            <div className='col-12'>
               <Button onClick={() => props.onClose()} size="sm">Close installer</Button>
            </div>
         </div>
         <div className='row'>
            <div className='col-12'>
               <Card className='m-1'>
                  <Card.Header>
                     <h5>Message to install on the platform</h5>
                  </Card.Header>
                  <Card.Body>
                     <MessageTemplateComponent template={props.template} preview={true} showId={false} />
                  </Card.Body>
               </Card>
            </div>
         </div>
         <div className="row">
            <div className='col-12'>
               <Card className='m-1'>
                  <Card.Header>
                     <h5>Automessage installation configuration</h5>
                  </Card.Header>
                  <Card.Body className="pt-1">
                     {recipes ?
                        <div className='row mb-1 mt-0'>
                           <div className='col-12 p-1'>
                              <AutoMessageRecipeSelector recipes={recipes} onSelect={applyRecipe} />
                           </div>
                        </div>
                        : ''
                     }
                     {errors ? <Alert variant='danger' className='m-0 my-1 p-1'><ul className='m-0 p-O ps-1 list-unstyled'>{errors.map((e, i) => <li key={i}><Icon name="exclamation-triangle" className='me-1'/>{e}</li>)}</ul></Alert> : ''}
                     <div className="row mx-1 p-0">
                        <FormGroup className='col-6 border border-radius p-1'>
                           <FormLabel><h5>Next time
                              <HelpTip content="Starting time for the message sending cycle" />
                              <span className="ms-1">( { showTimeRecipe(nextTime) }  )</span>
                           </h5>
                           </FormLabel>
                           <TimeRecipeWidget time={nextTime} label="Next Time" minDate={minDate} maxDate={maxDate} onChange={v => updateNextTime(v)} inSuffix="after installation" />
                           <div className='m-1'>
                              <TimeHelper onSelect={(duration) => { setNextTime({ in: duration }) }} className="me-1" />
                              <Button variant='info' size="sm" className="me-1" onClick={e => updateNextTime({ next: {} })} title="Next time expression">Next Exp.</Button>
                              <Button variant='info' size="sm" className="me-1" onClick={e => updateNextTime({ in: {} })} title="In some time expression (from installation time)">In Exp.</Button>
                              <Button variant='info' size="sm" className="me-1" onClick={e => updateNextTime(getUnixTime(Date.now()))} title="Fixed time and date">Fixed date</Button>
                           </div>
                        </FormGroup>

                        <FormGroup className='col-6 border border-radius p-1'>
                           <FormLabel>
                              <h5>Until time
                                 <HelpTip content="Ending time for the message sending cycle" />
                                 <span className="ms-1">( { untilTime ? showTimeRecipe(untilTime) : '-' }  )</span>
                              </h5>
                           </FormLabel>
                           {typeof (untilTime) != "undefined" ?
                              <TimeRecipeWidget time={untilTime} label="Until Time" minDate={minDate} maxDate={maxDate} onChange={v => updateUntilTime(v)} inSuffix="after installation"  />
                              : 'Vide'
                           }
                           <div className='m-1'>
                              <TimeHelper onSelect={(duration) => updateUntilTime({ 'in': duration })} />
                              <Button variant='info' size="sm" className="me-1" onClick={e => updateUntilTime(undefined)}>Empty</Button>
                              <Button variant='info' size="sm" className="me-1" onClick={e => updateUntilTime({ next: {} })} title="Next time expression">Next Exp.</Button>
                              <Button variant='info' size="sm" className="me-1" onClick={e => updateUntilTime(getUnixTime(Date.now()))} title="Next time expression">Fixed date</Button>
                           </div>
                        </FormGroup>
                     </div>
                     <FormGroup className='border border-radius p-1 mt-1'>
                        <FormLabel>
                           <h5>Periodicity <HelpTip content="Time interval in seconds between two sending" /></h5>
                        </FormLabel>
                        <Row>
                           <Col md='4'>
                              <input type="number" min={60} value={periodicity} onChange={e => setPeriodicity(+e.currentTarget.value)} className="form-control" />
                           </Col>
                           <Col>
                              {secondsToHuman(periodicity)}
                           </Col>
                        </Row>
                        <div className='m-1'>
                           <DurationHelper onSelect={(d) => setPeriodicity(d)} />
                        </div>
                     </FormGroup>

                     <div className='row mt-2'>
                        <FormGroup className='col-3'>
                           <FormLabel>Automessage Target Type</FormLabel>
                           <FormSelect onChange={e => setAutoTypeFromString(e.currentTarget.value)} value={autoType}>
                              {autoMessageTypes.map(v => <option key={v} value={v}>{v}</option>)}
                           </FormSelect>
                        </FormGroup>
                        <FormGroup className='col-3'>
                           <FormLabel>Message label <HelpTip content="Internal message label (used for logs)" /></FormLabel>
                           <FormControl onChange={e => setLabel(e.currentTarget.value)} defaultValue={label} />
                        </FormGroup>
                        <FormGroup className='col-3'>
                           <FormLabel>StudyKey<HelpTip content="Study key, will be used only for some type of automessage (study-participant), not used for 'all-users' type" /></FormLabel>
                           <DatalistInput
                              placeholder="Select study key"
                              label=""
                              value={studyKey}
                              setValue={setStudyKey}
                              items={studies.map((s) => { return { id: s, value: s } })}
                           />
                        </FormGroup>
                        <FormGroup>
                           <FormLabel>Condition to select participants targeted by the message</FormLabel>
                           <Card className='m-1 p-1'>
                              <Card.Body>
                                <ConditionWidget condition={condition} onChange={c => setCondition(c)} />
                              </Card.Body>
                           </Card>
                        </FormGroup>
                     </div>
                  </Card.Body>
               </Card>
            </div>
         </div>
         <div>
            { enableInstaller ? <Button variant='danger' onClick={handleInstall}>Install on the platform</Button> : <Button disabled={true} variant="disabled">Installation is not enabled yet</Button>}
         </div>
      </Card.Body>
   </Card>
}

interface TemplatePreviewProps {
   template: StoredMessageTemplate
   onClose: ()=>void;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = (props) => {
   return <Card className='mt-1'>
   <Card.Header className="bg-info border-info">
      <h5 className="text-white">Preview template "{props.template.internal_name}"</h5>
   </Card.Header>
   <Card.Body>
      <Button onClick={()=>props.onClose()} size="sm">Close preview</Button>
      <MessageTemplateComponent template={props.template} preview={true} showId={false} />
   </Card.Body>
</Card>
};


interface AutoMessagePageProps {
}

type AutoMessagePageMode = 'list' | 'install';

export const AutoMessagePage: React.FC<AutoMessagePageProps> = (props) => {

   const { data, error, isLoading, refetch } = useGetAllAutoMessagesQuery();
   const [mode, setMode] = useState<AutoMessagePageMode>('list');
   const [currentTemplate, setCurrentTemplate] = useState<StoredMessageTemplate | undefined>(undefined);
   const [installAutoMessage, installResult] = useInstallAutomessageMutation();
   const [showPreview, setShowPreview] = useState<boolean>(false);

   const handleRefresh = () => {
      refetch();
   }

   const handleClose = () => {
      setMode('list');
   }

   const handleInstall = (m: AutoMessage) => {
      console.log("Installing", m);
      installAutoMessage(m).then((res) => {
         console.log('install response', res);
         setCurrentTemplate(undefined);
         setMode('list');
         refetch();
      });
   }

   const handleClosePreview = ()=>{
      setShowPreview(false);
      setCurrentTemplate(undefined);
   };

   const actions = [
      { 'name': 'preview', label: "Preview", icon: "eye" },
      { 'name': 'install', label: "Install", icon: "upload", variant: 'warning' },
   ];

   const onAction = (name: string, m: EditableMessage) => {
      console.log(name, m);
      if (name === 'install') {
         setCurrentTemplate(m as StoredMessageTemplate);
         setMode('install');
      }
      if (name === 'preview') {
         setCurrentTemplate(m as StoredMessageTemplate);
         setShowPreview(true);
      }
   };

   return (<React.Fragment>
      <h1>Newsletters</h1>
      <Card >
         <Card.Header className="bg-info border-info">
            <h5 className='text-white'>Installed on the platform</h5>
         </Card.Header>
         <Card.Body>
            <div className='m-1'><Button onClick={handleRefresh} size="sm"><Icon name='sync' /> Refresh</Button></div>
            <LoaderComponent isLoading={isLoading} error={error} />
            {data ? <AutoMessageList automessages={data.autoMessages} /> : ''}
         </Card.Body>
      </Card>
      <UpdateResultComponent result={installResult} label="Installation" success="Message installed" error="Error during install of message" />

      {mode === 'list' ?
         <Card className='mt-1'>
            <Card.Header className='bg-primary border-primary' >
               <h5 className='text-white'>Templates in preparation (local)</h5>
            </Card.Header>
            <Card.Body>
               <MessageTemplatesListEditor collection='auto' allowedMessageTypes={['weekly']} actions={{ actions: actions, onAction: onAction }} highlight={currentTemplate} />
               { showPreview && currentTemplate ?
                  <TemplatePreview template={currentTemplate} onClose={handleClosePreview}/> 
               : ''
               }
            </Card.Body>
         </Card>
         : ''
      }
      {mode === 'install' && currentTemplate ?
         <AutoMessageInstaller template={currentTemplate} onClose={handleClose} onInstall={handleInstall} />
         : ''
      }
   </React.Fragment>)
};

